import { MdcInventoryDetail } from '../../../types/inventory-detail';
import { computed, linkedSignal, signal, Signal, WritableSignal } from '@angular/core';
import { InventoryQuantityControl } from '../../../components/inventory-quantity-control/inventory-quantity-control';
import { ActivatedRoute } from '@angular/router';
import { MdcInventoryItem, MdcInventoryProductData } from '../../../types/inventory-item';
import { MdcPalletItem, MdcPalletScanResult } from '@softapps/mdc/core';
import { base64Decode, isDefinedNotEmpty } from '@softline/core';

export abstract class InventoryDetailStrategy {
  abstract readonly quantityControlComponent: WritableSignal<
    Signal<InventoryQuantityControl | null | undefined>
  >;
  abstract readonly productDetails: WritableSignal<
    Signal<MdcInventoryDetail | null | undefined>
  >;

  abstract readonly qty: WritableSignal<number | null>;

  readonly itemId = signal<Signal<number | null>>(signal(null));
  readonly productId = signal<Signal<number | null>>(signal(null));
  readonly palletItemId = signal<Signal<number | null>>(signal(null));
  readonly currentInventoryingStorageId = signal<Signal<number | null>>(signal(null));
  readonly storageLocationDataQuery = signal<Signal<string | null>>(signal(null));
  readonly data = signal<Signal<any | null>>(signal(null));

  protected readonly storageLocationData = computed(() => {
    const data = this.storageLocationDataQuery()?.();
    if (isDefinedNotEmpty(data)) {
      return JSON.parse(base64Decode(data));
    }

    return null;
  });

  readonly unitEntryId = linkedSignal(() => {
    return (
      this.palletItem()?.idUnitEntry ??
      this.inventoryItem()?.productData?.unitEntryId ??
      this.quantityControlComponent()?.()?._unitEntryId() ??
      null
    );
  });

  readonly palletItem = computed(() => {
    const id = this.palletItemId()?.();
    if (!id) return null;
    return (
      ((this.palletData()?.pallet?.palettItems ?? []) as MdcPalletItem[]).find(
        (o) => o.id === id
      ) ?? null
    );
  });

  readonly unit = linkedSignal(
    () =>
      this.palletItem()?.quantityUnit ??
      this.inventoryItem()?.productData?.unit ??
      this.productDetails()?.()?.unit
  );

  readonly palletData = linkedSignal<MdcPalletScanResult | null>(() => {
    const data = this.data()?.();
    if (!data || !isDefinedNotEmpty(data)) return null;
    return JSON.parse(base64Decode(data));
  });

  readonly storageLocation = linkedSignal(() => {
    if (this.palletData()?.pallet?.storageLocation) {
      return this.palletData()?.pallet?.storageLocation;
    } else if (this.storageLocationData()) {
      return this.storageLocationData();
    } else if (this.inventoryItem()?.productData?.storageLocation) {
      return this.inventoryItem()?.productData?.storageLocation;
    }

    return null;
  });

  readonly sumUp = linkedSignal(() => {
    return (
      ((this.inventoryItem() as any)?.sumUp as boolean) ??
      this.productDetails()?.()?.lastInventory?.sumUp ??
      false
    );
  });

  readonly salePriceIncl = linkedSignal<number | null>(() => {
    const existingItem = this.inventoryItem();
    if (existingItem) return existingItem?.productData?.salePrice ?? null;

    return null;
  });

  readonly salePriceVisible = computed(() => {
    if (this.inventoryItem()) {
      return (
        this.productDetails()?.()?.product?.productType === 'S' &&
        !this.inventoryItem()?.productData?.manProduct
      );
    }

    return (
      this.productDetails()?.()?.product?.productType === 'S' &&
      !this.productDetails()?.()?.manProductByQuery
    );
  });

  readonly salePriceReadonly = computed(() => {
    return this.productDetails()?.()?.product?.productType !== 'S';
  });

  readonly salePriceRequired = computed(() => {
    if (this.inventoryItem()) {
      return (
        this.productDetails()?.()?.product?.productType === 'S' &&
        !this.inventoryItem()?.productData?.manProduct
      );
    }

    return (
      this.productDetails()?.()?.product?.productType === 'S' &&
      !this.productDetails()?.()?.manProductByQuery
    );
  });

  readonly route = signal<ActivatedRoute | null>(null);

  readonly inventoryItem: Signal<MdcInventoryItem | null> = signal(null);

  abstract delete(): Promise<boolean>;

  abstract confirm(
    params: {
      inventoryId: number;
      productId: number;
      stockId: number;
      unitId: number;
      sumUnitId: number | null;
      sumAmount: number | null;
      palletItemId?: number | null;
      pallet: any;
      quantity: number;
      productDetails: MdcInventoryDetail;
    },
    saveOnly: boolean
  ): Promise<void>;
}
